import React, { memo, useMemo } from 'react';
import PlayerAvatar from '../common/PlayerAvatar';
import EloChange from '../common/EloChange';
import RankMovementLabel from './RankMovementLabel';
import { shortenSurname } from '../../utils/nameUtils';
import { getPlayerFullName, getPlayerNameColor } from '../../utils/playerUtils';
import { getRankTier, RANK_TIER_CLASSES } from '../../utils/leaderboardRankTiers';

const HIGHLIGHT_CLASSES = {
  none: 'bg-surface-subtle border border-border-light',
  primary: 'bg-primary/[0.06] border border-primary/30 ring-1 ring-primary/20',
  accent: 'bg-accent/10 border border-accent/40 ring-1 ring-accent/20',
};

const VARIANT_CLASSES = {
  default: {
    card: 'rounded-full px-4 py-3 sm:px-5 sm:py-3.5',
    rank: 'h-10 w-10 text-sm',
    avatar: 'xl',
    name: 'text-base',
    elo: 'text-sm',
  },
  compact: {
    card: 'rounded-full px-3 py-2.5',
    rank: 'h-8 w-8 text-xs',
    avatar: 'md',
    name: 'text-sm',
    elo: 'text-xs',
  },
};

/**
 * Pill-style leaderboard row: rank + name/Elo on the left, avatar on the right.
 */
function LeaderboardRow({
  rank,
  player,
  elo,
  isCurrentUser = false,
  rankDelta = null,
  eloDelta = null,
  highlight = 'none',
  rankTierMode = 'none',
  variant = 'default',
  action = null,
  className = '',
}) {
  const styles = VARIANT_CLASSES[variant] || VARIANT_CLASSES.default;

  const displayName = useMemo(() => {
    if (isCurrentUser) return 'You';
    return shortenSurname(getPlayerFullName(player));
  }, [isCurrentUser, player]);

  const nameColor = isCurrentUser ? 'text-primary font-semibold' : getPlayerNameColor(player?.gender);
  const highlightKey = isCurrentUser ? highlight : 'none';
  const eloDisplay = elo != null ? `${Math.round(elo)} Elo` : '— Elo';
  const rankTier = getRankTier(rank, rankTierMode);
  const tierStyles = rankTier ? RANK_TIER_CLASSES[rankTier] : null;

  const cardClass = useMemo(() => {
    if (isCurrentUser && highlightKey !== 'none') {
      return HIGHLIGHT_CLASSES[highlightKey];
    }
    if (tierStyles?.card) return tierStyles.card;
    return HIGHLIGHT_CLASSES.none;
  }, [highlightKey, isCurrentUser, tierStyles]);

  const rankClass = tierStyles?.rank
    ? tierStyles.rank
    : 'border-border-medium text-text-primary';

  return (
    <div
      className={`flex items-center justify-between gap-3 ${styles.card} ${cardClass} ${className}`}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3 sm:gap-4">
        <div
          className={`flex shrink-0 items-center justify-center rounded-full border-2 font-semibold ${styles.rank} ${rankClass}`}
          aria-label={`Rank ${rank}`}
        >
          {rank}
        </div>

        <div className="min-w-0">
          <p className={`truncate font-semibold ${styles.name} ${nameColor}`} title={getPlayerFullName(player)}>
            {displayName}
          </p>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <p className={`tabular-nums text-text-secondary ${styles.elo}`}>{eloDisplay}</p>
            {action}
            {rankDelta != null && rankDelta !== 0 && (
              <RankMovementLabel rankDelta={rankDelta} size={variant === 'compact' ? 'xs' : 'xs'} />
            )}
            {eloDelta != null && eloDelta !== 0 && (
              <EloChange change={eloDelta} size="xs" showSign />
            )}
          </div>
        </div>
      </div>

      <PlayerAvatar player={player} size={styles.avatar} />
    </div>
  );
}

export default memo(LeaderboardRow);
